import type { JSX } from 'react';
import { AdminTabRedirect, UsersTabRedirect } from './pages/admin/redirects';
import { lazy, Suspense } from 'react';
import { Navigate, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';

import { Layout } from './components/Layout';
import { HomePage } from './pages/HomePage';
import { BlockPage } from './pages/BlockPage';
import { ArticlePage } from './pages/ArticlePage';
import { SearchPage } from './pages/SearchPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LoginPage } from './pages/LoginPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { OpeningPage } from './pages/OpeningPage';
import { RoomReadinessPage } from './pages/RoomReadinessPage';
import { ProfilePage } from './pages/ProfilePage';
import { CoursePage, EducationPage, QuizPage } from './pages/EducationPage';
import { PathPage } from './pages/PathPage';
import { FreshnessPage } from './pages/FreshnessPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { OpeningDashboardPage } from './pages/OpeningDashboardPage';
import { NotificationSettingsPage } from './pages/NotificationSettingsPage';
import { KnowledgeBasePage } from './pages/KnowledgeBasePage';
import { NewsPage } from './pages/NewsPage';
import { AskQuestionPage } from './pages/QuestionsPages';
import { InboxPage } from './pages/admin/InboxPage';
import { StandardsPage } from './pages/StandardsPage';
import { DiskPage } from './pages/DiskPage';
import { AiAssistantPage } from './pages/AiAssistantPage';
import { MyTasksPage } from './pages/MyTasksPage';
import { UsersPage } from './pages/UsersPage';
import { PointsPage } from './pages/PointsPage';
import { PartnerNetworkPage } from './pages/PartnerNetworkPage';
import { MyDossierPage } from './pages/MyDossierPage';
import { educationApi } from './lib/wikiApi';

// Тяжёлые admin/editor-страницы — отдельными чанками (аудит 2026-07-14:
// index-бандл 1.37MB; Tiptap-редактор и билдеры нужны только редакторам).
const CourseBuilderPage = lazy(() => import('./pages/CourseBuilderPage').then((m) => ({ default: m.CourseBuilderPage })));
const DecisionTreeBuilderPage = lazy(() => import('./pages/DecisionTreeBuilderPage').then((m) => ({ default: m.DecisionTreeBuilderPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const WikiAdminPage = lazy(() => import('./pages/WikiAdminPage').then((m) => ({ default: m.WikiAdminPage })));
const DesignSystemPage = lazy(() => import('./pages/DesignSystemPage').then((m) => ({ default: m.DesignSystemPage })));
const GraphPage = lazy(() => import('./pages/GraphPage').then((m) => ({ default: m.GraphPage })));
const LessonPage = lazy(() => import('./pages/LessonPage').then((m) => ({ default: m.LessonPage })));
const ReviewQueuePage = lazy(() => import('./pages/ReviewQueuePage').then((m) => ({ default: m.ReviewQueuePage })));
const PathsAdminPage = lazy(() => import('./pages/PathsAdminPage').then((m) => ({ default: m.PathsAdminPage })));
const MentorPage = lazy(() => import('./pages/MentorPage').then((m) => ({ default: m.MentorPage })));
const QodPage = lazy(() => import('./pages/QodPage').then((m) => ({ default: m.QodPage })));
// HUB 2.0 (W1 фаза 0): заглушки, наполняют W4.
const CourseWizardPage = lazy(() => import('./pages/CourseWizardPage').then((m) => ({ default: m.CourseWizardPage })));
const SkillsAdminPage = lazy(() => import('./pages/SkillsAdminPage').then((m) => ({ default: m.SkillsAdminPage })));


/**
 * Корневая разметка wiki.brasol.ru:
 *   /login           — LoginPage (без Layout — это standalone full-screen)
 *   /change-password — Принудительная смена при первом входе
 *   /                — HomePage
 *   /block/:code     — Список статей блока
 *   /article/:slug   — Детальная статья
 *   /search?q=...    — Поиск
 *   *                — 404
 *
 * /login и /change-password — БЕЗ Layout (свой full-screen дизайн).
 * Остальное — внутри Layout с header'ом.
 */

/** /article/:slug/edit → инлайн-редактор читателя (единый Tiptap). */
function EditRedirect(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/article/${encodeURIComponent(slug ?? '')}?edit=1`} replace />;
}

/**
 * /path без id (HUB 2.0, пункт меню «Траектория»): ведём на активную
 * траекторию (первая незавершённая, иначе последняя назначенная), а если
 * траекторий нет — на «Моё обучение». W3 может заменить на ветку PathPage.
 */
function PathIndexRedirect(): JSX.Element {
  const q = useQuery({ queryKey: ['wiki', 'education', 'paths'], queryFn: () => educationApi.myPaths(), retry: false });
  if (q.isLoading) return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  const paths = q.data ?? [];
  const active = paths.find((p) => !p.completedAt) ?? paths[0];
  return <Navigate to={active ? `/path/${encodeURIComponent(active.assignmentId)}` : '/education'} replace />;
}

export function App(): JSX.Element {
  const location = useLocation();
  const isStandalone =
    location.pathname === '/login' ||
    location.pathname === '/change-password';

  if (isStandalone) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<Spin style={{ display: 'block', margin: '80px auto' }} />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        {/* Навигация верхнего уровня FISHKA HUB (редизайн). */}
        <Route path="/knowledge" element={<KnowledgeBasePage />} />
        <Route path="/knowledge/ask" element={<AskQuestionPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/standards" element={<StandardsPage />} />
        <Route path="/disk" element={<DiskPage />} />
        <Route path="/ai" element={<AiAssistantPage />} />
        <Route path="/tasks" element={<MyTasksPage />} />
        {/* Открытие точки — спец-страница с прогрессом по этапам.
            Должна быть ВЫШЕ /block/:code чтобы не уйти на BlockPage. */}
        <Route path="/opening" element={<OpeningPage />} />
        <Route path="/opening/room-check" element={<RoomReadinessPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* Один редактор на всё (фаза 0.4, 2026-09-02): создание через модалку
            NewArticleModal и правка — инлайн Tiptap на /article/:slug?edit=1.
            Старый карточный BlockEditor удалён; /article/:slug/edit оставлен как
            редирект, чтобы не ломать ссылки (импорт-инбокс, закладки). */}
        <Route path="/article/:slug/edit" element={<EditRedirect />} />
        {/* LMS: курсы + модули + квизы */}
        <Route path="/education" element={<EducationPage />} />
        <Route path="/course/:id" element={<CoursePage />} />
        <Route path="/path" element={<PathIndexRedirect />} />
        <Route path="/path/:assignmentId" element={<PathPage />} />
        {/* Мои очки и бейджи (HUB 2.0, меню пользователя; экран — W3). */}
        <Route path="/points" element={<PointsPage />} />
        {/* «Досье партнёра» (FB-2, 31.10.2026): коллеги по сети и моя анкета. Доступ решает сервер (/api/wiki/dossier/*). */}
        <Route path="/partners" element={<PartnerNetworkPage />} />
        <Route path="/dossier" element={<MyDossierPage />} />
        <Route path="/course/:courseId/quiz/:quizId" element={<QuizPage />} />
        {/* Урок (фаза 2.1): статья/видео/тест/тренажёр. Старые ссылки
            /lesson/:moduleId работают — бэк отдаёт первый урок модуля. */}
        <Route path="/lesson/:lessonId" element={<LessonPage />} />
        {/* Наставник (фаза 3.1): чек-листы навыков для команды точки */}
        <Route path="/mentor" element={<MentorPage />} />
        <Route path="/qod" element={<QodPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/notifications" element={<NotificationSettingsPage />} />
        <Route path="/graph" element={<GraphPage />} />
        <Route path="/admin/archive" element={<AdminTabRedirect tab="archive" />} />
        <Route path="/admin/suggested-edits" element={<AdminTabRedirect tab="edits" />} />
        <Route path="/admin/decision-tree/:slug" element={<DecisionTreeBuilderPage />} />
        <Route path="/admin/documents" element={<AdminTabRedirect tab="templates" />} />
        <Route path="/admin/opening" element={<OpeningDashboardPage />} />
        <Route path="/admin/inbox" element={<AdminTabRedirect tab="import" />} />
        <Route path="/admin/courses" element={<CourseBuilderPage />} />
        {/* HUB 2.0: мастер нового курса и экран навыков (kb:admin, W4). */}
        <Route path="/admin/courses/new" element={<CourseWizardPage />} />
        <Route path="/admin/skills" element={<SkillsAdminPage />} />
        <Route path="/admin/reviews" element={<ReviewQueuePage />} />
        <Route path="/admin/paths" element={<PathsAdminPage />} />
        {/* «Обращения и вопросы»: pages/admin/InboxPage (W5), пока реэкспорт QuestionInboxPage. */}
        <Route path="/admin/questions" element={<InboxPage />} />
        <Route path="/admin/freshness" element={<FreshnessPage />} />
        <Route path="/admin/wiki" element={<WikiAdminPage />} />
        <Route path="/admin/roles" element={<UsersTabRedirect tab="roles" />} />
        <Route path="/admin/users" element={<UsersPage />} />
        <Route path="/admin/room-criteria" element={<AdminTabRedirect tab="criteria" />} />
        <Route path="/admin/analytics" element={<AnalyticsPage />} />
        {/* Дизайн-система — статичный референс визуального языка (для УК) */}
        <Route path="/admin/design" element={<DesignSystemPage />} />
        <Route path="/block/:code" element={<BlockPage />} />
        <Route path="/article/:slug" element={<ArticlePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </Layout>
  );
}
